import React, { useEffect, useState } from 'react';
import { Settings, HelpCircle, Heart, Calendar, CreditCard, LogOut, Edit2, Check, Plus, Trash2, ShoppingBag, Gift, Users, Coffee, Zap } from 'lucide-react';
import { UserProfile, SpecialDate } from '../../types';
import { MOCK_DATES, MOCK_GIFTS } from '../../data/mockData';
import { AccordionSection } from '../AccordionSection';
import { QRCodeDisplay } from '../QRCodeDisplay';
import { SpecialDateItem } from '../SpecialDateItem';
import { Button } from '../ui/Button';
import { useToast } from '../ToastContext';
import { triggerConfetti } from '../ui/ConfettiButton';
interface ProfileScreenProps {
  user: UserProfile;
  onNavigate?: (screen: string) => void;
  openSection?: string | null;
}
export function ProfileScreen({
  user,
  onNavigate,
  openSection
}: ProfileScreenProps) {
  const [dates, setDates] = useState<SpecialDate[]>(MOCK_DATES);
  const [birthday, setBirthday] = useState(user.birthday || '');
  const [isEditingBirthday, setIsEditingBirthday] = useState(false);
  const [birthdayInput, setBirthdayInput] = useState(birthday);
  const [showAddDate, setShowAddDate] = useState(false);
  const [newDate, setNewDate] = useState({
    name: '',
    date: '',
    type: 'other' as const,
    notes: ''
  });
  const {
    showToast
  } = useToast();
  const handleSaveBirthday = () => {
    if (!birthdayInput.match(/^\d{2}-\d{2}$/)) {
      showToast('Please use MM-DD format', 'error');
      return;
    }
    setBirthday(birthdayInput);
    setIsEditingBirthday(false);
    localStorage.setItem('userBirthday', birthdayInput);
    triggerConfetti();
    showToast('Birthday saved! 🎉', 'success');
  };
  const handleDeleteDate = (id: string) => {
    setDates(prev => prev.filter(d => d.id !== id));
    showToast('Date removed', 'info');
  };
  const handleAddDate = () => {
    if (!newDate.name || !newDate.date) {
      showToast('Please fill in name and date', 'error');
      return;
    }
    if (!newDate.date.match(/^\d{2}-\d{2}$/)) {
      showToast('Please use MM-DD format', 'error');
      return;
    }
    const date: SpecialDate = {
      id: `d-${Date.now()}`,
      ...newDate
    };
    setDates(prev => [...prev, date]);
    setNewDate({
      name: '',
      date: '',
      type: 'other',
      notes: ''
    });
    setShowAddDate(false);
    showToast('Date added successfully', 'success');
  };
  const formatBirthday = (dateStr: string) => {
    if (!dateStr) return 'Not set';
    const [month, day] = dateStr.split('-');
    const date = new Date(2000, parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric'
    });
  };
  // Mock orders data
  const recentOrders = [{
    id: '1',
    storeName: 'Blue Tokai Coffee',
    date: 'Today, 9:15 AM',
    total: 120,
    items: 'Filter Coffee'
  }, {
    id: '2',
    storeName: 'Cafe Madras',
    date: 'Yesterday',
    total: 180,
    items: 'Paneer Sandwich'
  }, {
    id: '3',
    storeName: 'Chai Point',
    date: '2 days ago',
    total: 90,
    items: 'Masala Chai + Samosa'
  }];
  // Get user's received gifts
  const receivedGifts = MOCK_GIFTS.filter(g => g.recipient === 'You');
  const pendingGifts = receivedGifts.filter(g => g.status === 'pending');
  const formatGiftDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  return <div className="pb-24 pt-6 px-5 animate-in fade-in duration-500" data-id="element-1625">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8" data-id="element-1626">
        <div className="w-20 h-20 rounded-full bg-gray-200 border-4 border-white shadow-md overflow-hidden" data-id="element-1627">
          <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" data-id="element-1628" />
        </div>
        <div data-id="element-1629">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1630">{user.name}</h1>
          <p className="text-gray-500" data-id="element-1631">{user.email}</p>
          <div className="flex items-center gap-2 mt-2" data-id="element-1632">
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded-full" data-id="element-1633">
              {user.points} Points
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-2" data-id="element-1634">
        <AccordionSection title="Your User ID" icon={<div className="w-5 h-5" data-id="element-1636" />} data-id="element-1635">
          <div className="flex justify-center pb-4" data-id="element-1637">
            <QRCodeDisplay value={user.id} avatar={user.avatar} data-id="element-1638" />
          </div>
          <p className="text-xs text-center text-gray-500 mt-2" data-id="element-1639">
            Let friends scan to send you gifts
          </p>
        </AccordionSection>

        <AccordionSection title="Special Dates" icon={<Calendar size={20} data-id="element-1641" />} data-id="element-1640">
          <div className="pb-4 space-y-4" data-id="element-1642">
            {/* My Birthday Section */}
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-100" data-id="element-1643">
              <div className="flex items-center justify-between mb-3" data-id="element-1644">
                <div className="flex items-center gap-2" data-id="element-1645">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-1646">
                    <Calendar size={16} className="text-purple-600" data-id="element-1647" />
                  </div>
                  <span className="font-bold text-gray-900" data-id="element-1648">My Birthday</span>
                </div>
                {!isEditingBirthday && <button onClick={() => {
                setIsEditingBirthday(true);
                setBirthdayInput(birthday);
              }} className="p-1.5 bg-white rounded-lg hover:bg-purple-100 transition-colors" data-id="element-1649">
                    <Edit2 size={14} className="text-purple-600" data-id="element-1650" />
                  </button>}
              </div>

              {isEditingBirthday ? <div className="flex items-center gap-2" data-id="element-1651">
                  <input type="text" placeholder="MM-DD" value={birthdayInput} onChange={e => setBirthdayInput(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-purple-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" maxLength={5} data-id="element-1652" />
                  <button onClick={handleSaveBirthday} className="p-2 bg-[#FFFC00] rounded-lg hover:bg-[#E6E300] transition-colors" data-id="element-1653">
                    <Check size={16} className="text-gray-900" strokeWidth={3} data-id="element-1654" />
                  </button>
                  <button onClick={() => setIsEditingBirthday(false)} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-1655">
                    <Trash2 size={16} className="text-gray-600" data-id="element-1656" />
                  </button>
                </div> : <p className="text-gray-700 font-medium" data-id="element-1657">
                  {formatBirthday(birthday)}
                </p>}
            </div>

            {/* Important Dates */}
            <div data-id="element-1658">
              <div className="flex items-center justify-between mb-3" data-id="element-1659">
                <p className="text-sm font-bold text-gray-500 uppercase" data-id="element-1660">
                  Important Dates
                </p>
                <button onClick={() => setShowAddDate(!showAddDate)} className="p-1.5 bg-[#FFFC00] rounded-lg hover:bg-[#E6E300] transition-colors" data-id="element-1661">
                  <Plus size={14} className="text-gray-900" strokeWidth={3} data-id="element-1662" />
                </button>
              </div>

              {showAddDate && <div className="bg-gray-50 rounded-xl p-4 mb-3 space-y-3 border border-gray-200" data-id="element-1663">
                  <input type="text" placeholder="Occasion name" value={newDate.name} onChange={e => setNewDate({
                ...newDate,
                name: e.target.value
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" data-id="element-1664" />
                  <input type="text" placeholder="MM-DD" value={newDate.date} onChange={e => setNewDate({
                ...newDate,
                date: e.target.value
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" maxLength={5} data-id="element-1665" />
                  <select value={newDate.type} onChange={e => setNewDate({
                ...newDate,
                type: e.target.value as any
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" data-id="element-1666">
                    <option value="birthday" data-id="element-1667">Birthday</option>
                    <option value="anniversary" data-id="element-1668">Anniversary</option>
                    <option value="holiday" data-id="element-1669">Holiday</option>
                    <option value="other" data-id="element-1670">Other</option>
                  </select>
                  <textarea placeholder="Notes (optional)" value={newDate.notes} onChange={e => setNewDate({
                ...newDate,
                notes: e.target.value
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm h-20 resize-none" data-id="element-1671" />
                  <div className="flex gap-2" data-id="element-1672">
                    <Button size="sm" onClick={handleAddDate} className="flex-1" data-id="element-1673">
                      Save
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setShowAddDate(false)} className="flex-1" data-id="element-1674">
                      Cancel
                    </Button>
                  </div>
                </div>}

              {dates.length > 0 ? dates.map(date => <SpecialDateItem key={date.id} date={date} onDelete={handleDeleteDate} data-id="element-1675" />) : <div className="text-center py-8 text-gray-400" data-id="element-1676">
                  <Calendar size={32} className="mx-auto mb-2 opacity-50" data-id="element-1677" />
                  <p className="text-sm" data-id="element-1678">No dates added yet</p>
                  <button onClick={() => setShowAddDate(true)} className="mt-2 text-sm text-purple-600 font-medium" data-id="element-1679">
                    Add Your First Date
                  </button>
                </div>}
            </div>
          </div>
        </AccordionSection>

        <AccordionSection title="Your Rewards" icon={<div className="w-5 h-5 bg-purple-100 rounded-full" data-id="element-1681" />} data-id="element-1680">
          <div className="space-y-3 pb-2" data-id="element-1682">
            <div className="p-3 bg-gray-50 rounded-xl" data-id="element-1683">
              <div className="flex justify-between items-center mb-2" data-id="element-1684">
                <span className="font-medium text-sm" data-id="element-1685">Bean & Leaf</span>
                <span className="text-sm font-bold text-purple-600" data-id="element-1686">
                  7/10 stamps
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1687">
                <div className="bg-purple-600 h-2 rounded-full" style={{
                width: '70%'
              }} data-id="element-1688" />
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl" data-id="element-1689">
              <div className="flex justify-between items-center mb-2" data-id="element-1690">
                <span className="font-medium text-sm" data-id="element-1691">Urban Garden</span>
                <span className="text-sm font-bold text-purple-600" data-id="element-1692">
                  3/10 stamps
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1693">
                <div className="bg-purple-600 h-2 rounded-full" style={{
                width: '30%'
              }} data-id="element-1694" />
              </div>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection title="Saved Cards" icon={<CreditCard size={20} data-id="element-1696" />} data-id="element-1695">
          <div className="p-4 bg-gray-50 rounded-xl mb-2 flex items-center gap-3" data-id="element-1697">
            <div className="w-10 h-6 bg-gray-800 rounded" data-id="element-1698" />
            <span className="font-medium text-sm" data-id="element-1699">•••• 4242</span>
            <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-bold" data-id="element-1700">
              Default
            </span>
          </div>
        </AccordionSection>

        <AccordionSection title="Favorite Places" icon={<Heart size={20} data-id="element-1702" />} data-id="element-1701">
          <div className="space-y-3 pb-2" data-id="element-1703">
            <div className="p-4 bg-pink-50 rounded-xl border border-pink-100 text-center" data-id="element-1704">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm" data-id="element-1705">
                <Heart size={24} className="text-pink-600" data-id="element-1706" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1" data-id="element-1707">
                Your fav cafe lists
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-1708">
                Places you have Been, To Go, custom collections. Mix-and-match
                your recommendations and share with friends.
              </p>
              <button onClick={() => onNavigate?.('favoritePlaces')} className="w-full py-3 bg-black text-[#FFFC00] rounded-xl font-bold text-sm active:scale-95 transition-transform shadow-md" data-id="element-1709">
                Open Favorites
              </button>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection title="Orders" icon={<ShoppingBag size={20} data-id="element-1711" />} data-id="element-1710">
          <div className="space-y-2 pb-2" data-id="element-1712">
            {recentOrders.map(order => <button key={order.id} onClick={() => onNavigate?.('orders')} className="w-full text-left p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-1713">
                <div className="flex items-center justify-between mb-1" data-id="element-1714">
                  <span className="font-medium text-sm" data-id="element-1715">{order.items}</span>
                  <span className="text-sm font-bold text-gray-900" data-id="element-1716">
                    ₹{order.total}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1717">
                  <span className="text-xs text-gray-500" data-id="element-1718">
                    {order.storeName}
                  </span>
                  <span className="text-xs text-gray-400" data-id="element-1719">{order.date}</span>
                </div>
              </button>)}
            <button onClick={() => onNavigate?.('orders')} className="w-full text-center py-2 text-sm text-purple-600 font-medium hover:text-purple-700 transition-colors" data-id="element-1720">
              View All Orders
            </button>
          </div>
        </AccordionSection>

        <AccordionSection title="Treat Wallet" icon={<Coffee size={20} data-id="element-1722" />} defaultOpen={openSection === 'giftWallet'} data-id="element-1721">
          <div className="space-y-2 pb-2" data-id="element-1723">
            {receivedGifts.length > 0 ? <>
                {receivedGifts.slice(0, 3).map(gift => <button key={gift.id} onClick={() => onNavigate?.('giftHistory')} className="w-full text-left p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-1724">
                    <div className="flex items-center gap-3" data-id="element-1725">
                      <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-1726">
                        <img src={gift.itemImage} alt={gift.itemName} className="w-full h-full object-cover" data-id="element-1727" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1728">
                        <div className="flex items-center justify-between mb-1" data-id="element-1729">
                          <span className="font-medium text-sm truncate" data-id="element-1730">
                            {gift.itemName}
                          </span>
                          {gift.status === 'pending' && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold flex-shrink-0 ml-2" data-id="element-1731">
                              New
                            </span>}
                        </div>
                        <div className="flex items-center justify-between" data-id="element-1732">
                          <span className="text-xs text-gray-500 truncate" data-id="element-1733">
                            From {gift.sender}
                          </span>
                          <span className="text-xs text-gray-400 flex-shrink-0 ml-2" data-id="element-1734">
                            {formatGiftDate(gift.date)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>)}
                <button onClick={() => onNavigate?.('giftHistory')} className="w-full text-center py-2 text-sm text-purple-600 font-medium hover:text-purple-700 transition-colors" data-id="element-1735">
                  View All Treats
                  {pendingGifts.length > 0 && <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold" data-id="element-1736">
                      {pendingGifts.length} pending
                    </span>}
                </button>
              </> : <div className="text-center py-6 text-gray-400" data-id="element-1737">
                <Coffee size={32} className="mx-auto mb-2 opacity-50" data-id="element-1738" />
                <p className="text-sm" data-id="element-1739">No treats yet</p>
              </div>}
          </div>
        </AccordionSection>

        <AccordionSection title="Drops" icon={<Zap size={20} data-id="element-1741" />} data-id="element-1740">
          <div className="space-y-3 pb-2" data-id="element-1742">
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 text-center" data-id="element-1743">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm" data-id="element-1744">
                <Zap size={24} className="text-amber-600" data-id="element-1745" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1" data-id="element-1746">
                Exclusive Drops & Deals
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-1747">
                Limited-time offers from your favorite spots. Grab them before
                they're gone!
              </p>
              <button onClick={() => onNavigate?.('drops')} className="w-full py-3 bg-black text-[#FFFC00] rounded-xl font-bold text-sm active:scale-95 transition-transform shadow-md" data-id="element-1748">
                View Drops
              </button>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection title="Host a Gathering" icon={<Heart size={20} data-id="element-1750" />} data-id="element-1749">
          <div className="space-y-3 pb-2" data-id="element-1751">
            <div className="p-4 bg-purple-50 rounded-xl border border-purple-100 text-center" data-id="element-1752">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm" data-id="element-1753">
                <Users size={24} className="text-purple-600" data-id="element-1754" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1" data-id="element-1755">Be a Host</h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-1756">
                Book a spot for your team, chess club, or a casual meetup. Small
                groups only!
              </p>
              <button onClick={() => onNavigate?.('hostGathering')} className="w-full py-3 bg-black text-[#FFFC00] rounded-xl font-bold text-sm active:scale-95 transition-transform shadow-md" data-id="element-1757">
                Find a Gathering Spot
              </button>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection title="Make a Plan" icon={<Users size={20} data-id="element-1759" />} data-id="element-1758">
          <div className="space-y-3 pb-2" data-id="element-1760">
            <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-100 text-center" data-id="element-1761">
              <div className="w-12 h-12 bg-[#FFFC00] rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm" data-id="element-1762">
                <Coffee size={24} className="text-black" data-id="element-1763" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1" data-id="element-1764">
                Coordinate with friends
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-1765">
                Pick a place, suggest times, and lock it in without the group
                chat chaos.
              </p>
              <button onClick={() => onNavigate?.('plan')} className="w-full py-3 bg-black text-[#FFFC00] rounded-xl font-bold text-sm active:scale-95 transition-transform shadow-md" data-id="element-1766">
                Make a Plan
              </button>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection title="Help & Support" icon={<HelpCircle size={20} data-id="element-1768" />} data-id="element-1767">
          <div className="space-y-2 pb-2" data-id="element-1769">
            <button className="w-full text-left p-3 bg-gray-50 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors" data-id="element-1770">
              FAQ
            </button>
            <button className="w-full text-left p-3 bg-gray-50 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors" data-id="element-1771">
              Contact Support
            </button>
          </div>
        </AccordionSection>

        <div className="pt-6" data-id="element-1772">
          <button className="flex items-center gap-3 text-red-500 font-medium px-1 hover:text-red-600 transition-colors" data-id="element-1773">
            <LogOut size={20} data-id="element-1774" /> Sign Out
          </button>
        </div>
      </div>
    </div>;
}